/**
 * GroupMenuPickerPage — Iter 197 + iter 198.
 *
 * The pre-order menu picker for a group dinner attendee.
 *
 *   - Fetches the restaurant menu (business_id from group state) via
 *     the `X-Business-Id` header so multi-tenant routing works.
 *   - Menu is grouped by category with collapsible sections and a
 *     sticky search bar + dietary chips (veg / vegan / GF / halal /
 *     dairy-free) so restaurants with 100+ items stay usable.
 *   - Advance-prep items get a red "Order in advance" badge and, if
 *     `requires_prepayment=true`, a "Prepay now to lock" CTA.
 *   - Attendee picks quantities + optional notes.
 *   - Bill nomination dropdown: "Add to host's tab" (default), "I'll
 *     pay myself at the venue" (self_paying), or transfer to a
 *     specific attendee (paid_by_attendee_id).
 *   - Submits to `POST /group-dining/{gid}/preorders`.
 *   - If needs_prepay, immediately opens Stripe Checkout via
 *     `POST /group-dining/{gid}/prepay`.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { api, formatApiError } from "@/lib/api";

function fmtMoney(cents) {
  const n = Number(cents || 0) / 100;
  return n.toLocaleString("en-AU", {
    style: "currency",
    currency: "AUD",
  });
}

const DIETARY_CHIPS = [
  { key: "vegetarian",  label: "Veg",       tags: ["vegetarian", "veg"] },
  { key: "vegan",       label: "Vegan",     tags: ["vegan"] },
  { key: "gluten_free", label: "GF",        tags: ["gluten_free", "gluten-free", "gf"] },
  { key: "dairy_free",  label: "Dairy-free", tags: ["dairy_free", "dairy-free", "df"] },
  { key: "halal",       label: "Halal",      tags: ["halal"] },
];

function matchesDiet(item, diet) {
  if (!diet) return true;
  const meta = DIETARY_CHIPS.find((d) => d.key === diet);
  if (!meta) return true;
  const tags = (item.tags || []).map((t) => String(t).toLowerCase());
  return meta.tags.some((t) => tags.includes(t));
}

export default function GroupMenuPickerPage() {
  const { gid } = useParams();
  const [params] = useSearchParams();
  const t = params.get("t") || "";
  const a = params.get("a") || "";
  const navigate = useNavigate();

  const [group, setGroup] = useState(null);
  const [me, setMe] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [menu, setMenu] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cart, setCart] = useState({}); // item_id -> {qty, notes}
  const [payMode, setPayMode] = useState("host"); // host | self | <attendee_id>
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState("");
  const [dietFilter, setDietFilter] = useState(""); // "" | vegetarian | vegan | ...
  const [collapsed, setCollapsed] = useState({});   // cat_id -> boolean

  const qs = useMemo(() => {
    const q = [];
    if (t) q.push(`t=${encodeURIComponent(t)}`);
    if (a) q.push(`a=${encodeURIComponent(a)}`);
    return q.length ? `?${q.join("&")}` : "";
  }, [t, a]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(
        `/group-dining/${encodeURIComponent(gid)}${qs}`,
      );
      setGroup(data.group);
      setMe(data.me);
      setAttendees(data.attendees || []);
      const bizId = data.group.business_id;
      // Multi-tenant: send business_id via header so `get_business_id`
      // scopes to the restaurant we're pre-ordering from.
      const headers = { "X-Business-Id": bizId };
      const [{ data: mdata }, { data: cdata }] = await Promise.all([
        api.get(`/menu?available_only=true`, { headers }),
        api.get(`/categories`, { headers }),
      ]);
      const items = Array.isArray(mdata) ? mdata : mdata?.items || [];
      setMenu(items);
      setCategories(Array.isArray(cdata) ? cdata : []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  }, [gid, qs]);

  useEffect(() => { load(); }, [load]);

  const bump = (id, delta) => {
    setCart((c) => {
      const cur = c[id]?.qty || 0;
      const next = Math.max(0, cur + delta);
      const nextRow = { ...(c[id] || {}), qty: next };
      const out = { ...c };
      if (next === 0) delete out[id];
      else out[id] = nextRow;
      return out;
    });
  };

  const setNote = (id, notes) => {
    setCart((c) => ({ ...c, [id]: { ...(c[id] || { qty: 1 }), notes } }));
  };

  const cartLines = useMemo(() => {
    return Object.entries(cart).map(([id, row]) => {
      const item = menu.find((m) => m.id === id) || {};
      return {
        menu_item_id: id,
        name: item.name || "Item",
        qty: row.qty,
        price_cents: Math.round(Number(item.price || 0) * 100),
        notes: row.notes || null,
        _advance: !!item.advance_prep_required,
        _prepay: !!item.requires_prepayment,
      };
    });
  }, [cart, menu]);

  const total = cartLines.reduce(
    (s, l) => s + l.qty * l.price_cents,
    0,
  );
  const hasPrepay = cartLines.some((l) => l._prepay);

  // Filter + group menu by category
  const grouped = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const filtered = menu.filter((m) => {
      if (needle) {
        const hay = (
          (m.name || "") + " " + (m.description || "") + " " + (m.origin || "")
        ).toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      if (dietFilter && !matchesDiet(m, dietFilter)) return false;
      return true;
    });
    // Preserve category order from the categories array; unknown-cat items
    // land in a trailing "Other" bucket.
    const catOrder = categories
      .slice()
      .sort((x, y) => (x.order || 0) - (y.order || 0));
    const bucketsById = new Map();
    for (const c of catOrder) {
      bucketsById.set(c.id, { id: c.id, name: c.name, items: [] });
    }
    const other = { id: "__other__", name: "Other", items: [] };
    for (const m of filtered) {
      const b = bucketsById.get(m.category_id) || other;
      b.items.push(m);
    }
    const out = [...bucketsById.values()].filter((b) => b.items.length);
    if (other.items.length) out.push(other);
    return out;
  }, [menu, categories, search, dietFilter]);

  const submit = async () => {
    if (!cartLines.length) return toast.error("Pick at least one dish.");
    setSubmitting(true);
    try {
      const payload = {
        items: cartLines.map((l) => ({
          menu_item_id: l.menu_item_id,
          name: l.name,
          qty: l.qty,
          price_cents: l.price_cents,
          notes: l.notes,
        })),
        self_paying: payMode === "self",
        paid_by_attendee_id:
          payMode !== "host" && payMode !== "self" ? payMode : null,
      };
      const { data } = await api.post(
        `/group-dining/${encodeURIComponent(gid)}/preorders${qs}`,
        payload,
      );
      toast.success("Pre-order saved");
      // If any item requires prepayment, kick straight to Stripe.
      if (data.needs_prepay) {
        try {
          const { data: pay } = await api.post(
            `/group-dining/${encodeURIComponent(gid)}/prepay${qs}`,
            { order_id: data.order_id },
          );
          if (pay?.checkout_url) {
            window.location.href = pay.checkout_url;
            return;
          }
        } catch (e) {
          toast.error(
            "Order saved but prepayment couldn't start: " +
              (formatApiError(e.response?.data?.detail) || e.message),
          );
        }
      }
      navigate(`/group-dining/${gid}${qs}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSubmitting(false); }
  };

  if (!group) {
    return (
      <div
        data-testid="grp-menu-loading"
        className="min-h-screen flex items-center justify-center text-slate-500"
      >
        Loading menu…
      </div>
    );
  }

  const toggleCat = (id) =>
    setCollapsed((c) => ({ ...c, [id]: !c[id] }));

  return (
    <div data-testid="grp-menu-page" className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-4">
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <div className="text-xs uppercase tracking-wide text-slate-500">
            Pre-order for
          </div>
          <h1 className="text-xl font-semibold text-slate-900 mt-1">
            {group.business_name}
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Regular items are changeable at the restaurant. Advance-prep
            dishes (Peking Duck, live lobster…) are locked once paid —
            no refunds.
          </p>
        </div>

        {/* Sticky search + diet chips */}
        <div
          data-testid="grp-menu-toolbar"
          className="sticky top-2 z-10 bg-white/95 backdrop-blur border border-slate-200 rounded-2xl p-3 shadow-sm"
        >
          <div className="flex items-center gap-2">
            <Icons.Search size={16} className="text-slate-400 shrink-0 ml-1" />
            <input
              data-testid="grp-menu-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search dishes, ingredients, cuisine…"
              className="w-full text-sm outline-none py-1"
            />
            {search && (
              <button
                data-testid="grp-menu-search-clear"
                onClick={() => setSearch("")}
                className="text-slate-400 hover:text-slate-600"
              >
                <Icons.X size={14} />
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {DIETARY_CHIPS.map((d) => {
              const active = dietFilter === d.key;
              return (
                <button
                  key={d.key}
                  data-testid={`grp-menu-diet-${d.key}`}
                  onClick={() => setDietFilter(active ? "" : d.key)}
                  className={
                    "text-xs px-2.5 py-1 rounded-full border transition " +
                    (active
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white text-slate-600 border-slate-200 hover:border-slate-300")
                  }
                >
                  {d.label}
                </button>
              );
            })}
            {dietFilter && (
              <button
                data-testid="grp-menu-diet-clear"
                onClick={() => setDietFilter("")}
                className="text-xs px-2.5 py-1 rounded-full text-slate-400 hover:text-slate-600"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        <div data-testid="grp-menu-list" className="space-y-3">
          {grouped.length === 0 && (
            <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center text-slate-400 text-sm">
              {menu.length === 0
                ? "This restaurant hasn't published its menu yet."
                : "No dishes match your search."}
            </div>
          )}
          {grouped.map((cat) => {
            const isCollapsed = !!collapsed[cat.id];
            return (
              <div
                key={cat.id}
                data-testid={`grp-menu-cat-${cat.id}`}
                className="bg-white border border-slate-200 rounded-2xl overflow-hidden"
              >
                <button
                  data-testid={`grp-menu-cat-toggle-${cat.id}`}
                  onClick={() => toggleCat(cat.id)}
                  className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-50 transition"
                >
                  <span className="font-medium text-slate-800">
                    {cat.name}
                    <span className="ml-2 text-xs text-slate-400 font-normal">
                      {cat.items.length}
                    </span>
                  </span>
                  <Icons.ChevronDown
                    size={16}
                    className={
                      "text-slate-400 transition-transform " +
                      (isCollapsed ? "-rotate-90" : "")
                    }
                  />
                </button>
                {!isCollapsed && (
                  <div className="divide-y divide-slate-100">
                    {cat.items.map((m) => {
                      const qty = cart[m.id]?.qty || 0;
                      const price = Math.round(Number(m.price || 0) * 100);
                      return (
                        <div
                          key={m.id}
                          data-testid={`grp-menu-item-${m.id}`}
                          className="p-4 flex items-start justify-between gap-4"
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-slate-800">
                                {m.name}
                              </span>
                              {m.advance_prep_required && (
                                <span
                                  data-testid={`grp-menu-adv-badge-${m.id}`}
                                  className="text-xs px-1.5 py-0.5 rounded bg-rose-50 border border-rose-200 text-rose-800"
                                >
                                  Order in advance
                                </span>
                              )}
                              {m.requires_prepayment && (
                                <span className="text-xs px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-900">
                                  Prepay • final
                                </span>
                              )}
                              {(m.tags || []).slice(0, 3).map((tg) => (
                                <span
                                  key={tg}
                                  className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500"
                                >
                                  {tg}
                                </span>
                              ))}
                            </div>
                            {m.description && (
                              <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                                {m.description}
                              </p>
                            )}
                            {m.advance_prep_note && (
                              <p className="text-xs text-rose-700 mt-1">
                                {m.advance_prep_note}
                              </p>
                            )}
                            {qty > 0 && (
                              <input
                                data-testid={`grp-menu-note-${m.id}`}
                                value={cart[m.id]?.notes || ""}
                                onChange={(e) => setNote(m.id, e.target.value)}
                                placeholder="Notes (allergies, cooking pref)"
                                className="mt-2 w-full text-xs border border-slate-200 rounded px-2 py-1"
                              />
                            )}
                          </div>
                          <div className="text-right flex flex-col items-end gap-1">
                            <div className="text-sm text-slate-700">
                              {fmtMoney(price)}
                            </div>
                            <div className="inline-flex items-center border border-slate-200 rounded-lg overflow-hidden">
                              <button
                                data-testid={`grp-menu-minus-${m.id}`}
                                onClick={() => bump(m.id, -1)}
                                className="w-7 h-7 text-slate-600 hover:bg-slate-50"
                              >
                                −
                              </button>
                              <span className="w-6 text-center text-sm">{qty}</span>
                              <button
                                data-testid={`grp-menu-plus-${m.id}`}
                                onClick={() => bump(m.id, +1)}
                                className="w-7 h-7 text-slate-600 hover:bg-slate-50"
                              >
                                +
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Bill nomination */}
        {cartLines.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <h3 className="text-sm font-semibold text-slate-700 mb-2">
              Who&apos;s paying this tab?
            </h3>
            <select
              data-testid="grp-menu-paymode"
              value={payMode}
              onChange={(e) => setPayMode(e.target.value)}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white"
            >
              <option value="host">
                Add to {group.host_name}&apos;s tab (default)
              </option>
              <option value="self">I&apos;ll pay myself at the venue</option>
              {attendees
                .filter(
                  (att) =>
                    att.rsvp === "accepted" &&
                    !att.is_host &&
                    att.id !== me?.id,
                )
                .map((att) => (
                  <option key={att.id} value={att.id}>
                    Transfer to {att.invitee_name}
                  </option>
                ))}
            </select>
            <p className="text-xs text-slate-500 mt-2">
              Transfer to another guest works only if they&apos;ve
              accepted the invite. Advance-prep prepayment always
              comes off the ordering guest&apos;s card.
            </p>
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-2xl p-6 sticky bottom-4 shadow-lg">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-xs text-slate-500 uppercase tracking-wide">
                Total
              </div>
              <div className="text-2xl font-semibold text-slate-900">
                {fmtMoney(total)}
              </div>
              {hasPrepay && (
                <div className="text-xs text-amber-800 mt-1">
                  Includes prepay-required items — you&apos;ll be sent
                  to Stripe on Save.
                </div>
              )}
            </div>
            <button
              data-testid="grp-menu-submit-btn"
              onClick={submit}
              disabled={submitting || cartLines.length === 0}
              className="px-5 py-3 rounded-lg bg-slate-900 text-white font-semibold disabled:opacity-40"
            >
              {submitting
                ? "Saving…"
                : hasPrepay
                ? "Save + prepay"
                : "Save pre-order"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
